import React, { useState, useEffect, memo, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  PlusIcon,
  PlayIcon,
  StopIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PauseIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SparklesIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../services/api';
import toast from 'react-hot-toast';

const MassDMs = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [scrapedData, setScrapedData] = useState({ tags: [], sessions: [] });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [bulkMessages, setBulkMessages] = useState('');
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processId, setProcessId] = useState(null);
  const [pendingTaskData, setPendingTaskData] = useState(null);
  const [serviceUrls, setServiceUrls] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    accountIds: [],
    recipientSource: 'manual', // 'manual' or 'scraped'
    manualRecipients: '',
    scrapedDataTag: '',
    scrapedDataSessionId: '',
    messages: [],
    settings: {
      delayMin: 10,
      delayMax: 30,
      restInterval: 600,
      spamPauseDuration: 86400,
      messagesPerBatch: 15,
      batchPauseDuration: 600,
      maxRequestsPerDay: 350,
      sendMethod: 'rest' // Always uses REST API (Messages endpoint)
    }
  });

  const { socket } = useSocket();

  // Track which dropdowns are open by task ID (persists across re-renders)
  const [openDropdowns, setOpenDropdowns] = useState(new Set());
  
  // Stable toggle function that doesn't change on re-renders
  const handleToggleDropdown = useCallback((taskId) => {
    setOpenDropdowns(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  // Batch socket updates to prevent re-render spam
  const updateQueueRef = useRef(new Map()); // taskId -> update data
  const updateTimeoutRef = useRef(null);

  useEffect(() => {
    // Initial load only
    const loadServiceUrls = async () => {
      try {
        const response = await api.get('/config/services');
        setServiceUrls(response.data || {});
      } catch (error) {
        console.error('[MassDMs] Error loading service URLs:', error);
      }
    };
    
    loadServiceUrls();
    loadTasks();
    loadAccounts();
    loadScrapedData();
    
    // Remove polling - use sockets only
  }, []);

  useEffect(() => {
    if (!socket) return;

    // Join room for targeted socket updates
    socket.emit('join-room', 'mass-dms-page');

    // Batch handler: collects updates, applies once per interval
    const flushUpdates = () => {
      if (updateQueueRef.current.size === 0) return;
      
      setTasks(prevTasks => {
        const updated = [...prevTasks];
        let changed = false;
        
        updateQueueRef.current.forEach((update, taskId) => {
          const index = updated.findIndex(t => t._id === taskId);
          if (index >= 0) {
            // Merge update efficiently
            updated[index] = {
              ...updated[index],
              ...update,
              progress: {
                ...updated[index].progress,
                ...(update.progress || {})
              },
              // Merge accountStatus if provided
              ...(update.accountStatus && { accountStatus: update.accountStatus })
            };
            changed = true;
          }
        });
        
        updateQueueRef.current.clear();
        return changed ? updated : prevTasks;
      });
      
      updateTimeoutRef.current = null;
    };

    // Progress updates (batched)
    socket.on('mass-dm-progress', (data) => {
      // Queue update instead of applying immediately
      const existing = updateQueueRef.current.get(data.taskId) || {};
      updateQueueRef.current.set(data.taskId, {
        ...existing,
        progress: {
          ...existing.progress,
          ...data.progress
        },
        // Include accountStatus if provided
        ...(data.accountStatus && { accountStatus: data.accountStatus })
      });
      
      // Debounce: flush after 500ms of no new updates
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTimeoutRef.current = setTimeout(flushUpdates, 500);
    });

    // Task status updates (immediate, minimal)
    socket.on('mass-dm-task-updated', (data) => {
      setTasks(prevTasks => {
        const index = prevTasks.findIndex(t => t._id === data.taskId);
        if (index >= 0) {
          const updated = [...prevTasks];
          // Only update changed fields
          updated[index] = {
            ...updated[index],
            status: data.status,
            ...(data.progress && { progress: { ...updated[index].progress, ...data.progress } }),
            // Include accountStatus if provided (for live account status updates)
            ...(data.accountStatus && { accountStatus: data.accountStatus })
          };
          return updated;
        }
        // New task - reload once
        loadTasks();
        return prevTasks;
      });
    });

    // Account paused (immediate notification)
    socket.on('mass-dm-account-paused', (data) => {
      toast.error(`Account paused: ${data.reason}`, { icon: '⚠️' });
      // Don't update task status - only the account is paused, not the entire task
      // The account status will be updated via mass-dm-progress or mass-dm-task-updated events
    });

    return () => {
      socket.emit('leave-room', 'mass-dms-page');
      socket.off('mass-dm-progress');
      socket.off('mass-dm-task-updated');
      socket.off('mass-dm-account-paused');
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [socket]);

  const loadTasks = async () => {
    try {
      const response = await api.get('/mass-dm/tasks');
      setTasks(response.data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      // Don't show error toast - just set empty array
      // Users without permission will see empty list, which is fine
      setTasks([]);
      // Only show error for actual server errors (500), not permission issues
      if (error.response?.status >= 500) {
        toast.error('Failed to load tasks');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await api.get('/mass-dm/accounts');
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadScrapedData = async () => {
    try {
      const response = await api.get('/mass-dm/scraped-data');
      setScrapedData(response.data);
    } catch (error) {
      console.error('Error loading scraped data:', error);
    }
  };

  const handleCreateTask = async () => {
    try {
      // Validation
      if (!formData.name.trim()) {
        toast.error('Task name is required');
        return;
      }

      if (formData.accountIds.length === 0) {
        toast.error('Please select at least one account');
        return;
      }

      if (formData.messages.filter(m => m.trim()).length === 0) {
        toast.error('Please add at least one message');
        return;
      }

      // If manual recipients, process them first
      if (formData.recipientSource === 'manual') {
        const recipients = formData.manualRecipients
          .split('\n')
          .map(r => r.trim().replace('@', ''))
          .filter(r => r.length > 0);
        
        if (recipients.length === 0) {
          toast.error('Please add at least one recipient');
          return;
        }

        // Generate a tag for this batch
        const tag = `manual_${Date.now()}`;
        
        // Store task data to create after processing
        setPendingTaskData({
          name: formData.name,
          accountIds: formData.accountIds,
          recipientSource: 'scraped', // Change to scraped since we're storing in ScrapedFollower
          scrapedDataTag: tag,
          messages: formData.messages.filter(m => m.trim()),
          settings: formData.settings
        });

        // Start processing recipients
        try {
          const response = await api.post('/mass-dm/process-recipients', {
            recipients,
            tag
          });
          
          setProcessId(response.data.processId);
          setShowProcessModal(true);
        } catch (error) {
          console.error('Error starting recipient processing:', error);
          toast.error(error.response?.data?.error || 'Failed to process recipients');
          setPendingTaskData(null);
        }
        
        return; // Don't create task yet, wait for processing to complete
      }

      // For scraped data, create task directly
      let payload = {
        name: formData.name,
        accountIds: formData.accountIds,
        recipientSource: formData.recipientSource,
        messages: formData.messages.filter(m => m.trim()),
        settings: formData.settings
      };

      if (formData.recipientSource === 'scraped') {
        if (!formData.scrapedDataTag) {
          toast.error('Please select scraped data source');
          return;
        }
        payload.scrapedDataTag = formData.scrapedDataTag;
        if (formData.scrapedDataSessionId) {
          payload.scrapedDataSessionId = formData.scrapedDataSessionId;
        }
      }

      await api.post('/mass-dm/tasks', payload);
      toast.success('Task created successfully');
      setShowCreateModal(false);
      resetForm();
      loadTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(error.response?.data?.error || 'Failed to create task');
    }
  };

  // Handle completion of recipient processing
  const handleRecipientProcessComplete = async (results) => {
    setShowProcessModal(false);
    
    if (!pendingTaskData) {
      toast.error('Task data not found');
      return;
    }

    try {
      // Create the task with the processed recipients (now stored as scraped data)
      await api.post('/mass-dm/tasks', pendingTaskData);
      
      toast.success(
        `Task created successfully! ${results.stored} recipients stored, ${results.duplicates} duplicates skipped.`
      );
      
      setShowCreateModal(false);
      resetForm();
      setPendingTaskData(null);
      loadTasks();
    } catch (error) {
      console.error('Error creating task after processing:', error);
      toast.error(error.response?.data?.error || 'Failed to create task');
      setPendingTaskData(null);
    }
  };

  const handleStartTask = async (taskId) => {
    // Optimistically update UI immediately
    setTasks(prevTasks => {
      const index = prevTasks.findIndex(t => t._id === taskId);
      if (index >= 0) {
        const updated = [...prevTasks];
        updated[index] = { ...updated[index], status: 'running' };
        return updated;
      }
      return prevTasks;
    });

    try {
      const serviceUrl = serviceUrls.massdm;
      
      if (!serviceUrl) {
        console.error('[MassDMs] No massdm service URL available');
        toast.error('MassDM service not available');
        loadTasks();
        return;
      }
      
      await axios.post(`${serviceUrl}/api/mass-dm/tasks/${taskId}/start`, {}, {
        timeout: 60000,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast.success('Task started');
      // Don't reload immediately - let socket event update the status
      // Only reload if socket event doesn't come within 2 seconds
      setTimeout(() => {
        setTasks(prevTasks => {
          const task = prevTasks.find(t => t._id === taskId);
          // Only reload if status is still not 'running' (socket event might have failed)
          if (task && task.status !== 'running' && task.status !== 'pending') {
            loadTasks();
          }
          return prevTasks;
        });
      }, 2000);
    } catch (error) {
      console.error('[MassDMs] Error starting task:', error);
      toast.error(error.response?.data?.error || 'Failed to start task');
      // Revert optimistic update on error
      loadTasks();
    }
  };

  const handleStopTask = async (taskId) => {
    // Optimistically update UI immediately
    setTasks(prevTasks => {
      const index = prevTasks.findIndex(t => t._id === taskId);
      if (index >= 0) {
        const updated = [...prevTasks];
        updated[index] = { ...updated[index], status: 'stopped' };
        return updated;
      }
      return prevTasks;
    });

    try {
      const serviceUrl = serviceUrls.massdm;
      
      if (!serviceUrl) {
        console.error('[MassDMs] No massdm service URL available');
        toast.error('MassDM service not available');
        loadTasks();
        return;
      }
      
      await axios.post(`${serviceUrl}/api/mass-dm/tasks/${taskId}/stop`, {}, {
        timeout: 60000,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      toast.success('Task stopped');
      // Reload to get latest data from server
      loadTasks();
    } catch (error) {
      console.error('[MassDMs] Error stopping task:', error);
      toast.error(error.response?.data?.error || 'Failed to stop task');
      // Revert optimistic update on error
      loadTasks();
    }
  };

  const handlePauseTask = async (taskId) => {
    // Optimistically update UI immediately
    setTasks(prevTasks => {
      const index = prevTasks.findIndex(t => t._id === taskId);
      if (index >= 0) {
        const updated = [...prevTasks];
        updated[index] = { ...updated[index], status: 'paused' };
        return updated;
      }
      return prevTasks;
    });

    try {
      await api.post(`/mass-dm/tasks/${taskId}/pause`);
      toast.success('Task paused');
      // Don't reload immediately - let socket event update the status
      // Only reload if socket event doesn't come within 2 seconds
      setTimeout(() => {
        setTasks(prevTasks => {
          const task = prevTasks.find(t => t._id === taskId);
          // Only reload if status is still not 'paused' (socket event might have failed)
          if (task && task.status !== 'paused') {
            loadTasks();
          }
          return prevTasks;
        });
      }, 2000);
    } catch (error) {
      console.error('Error pausing task:', error);
      toast.error(error.response?.data?.error || 'Failed to pause task');
      // Revert optimistic update on error
      loadTasks();
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await api.delete(`/mass-dm/tasks/${taskId}`);
      toast.success('Task deleted');
      loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error(error.response?.data?.error || 'Failed to delete task');
    }
  };

  const handleResetSpamAccounts = async (taskId) => {
    try {
      const response = await api.post(`/mass-dm/tasks/${taskId}/reset-spam-accounts`);
      if (response.data.resetCount > 0) {
        toast.success(`Reset ${response.data.resetCount} account(s) from spam/rate limit`);
      } else {
        toast.info('No spam/rate limited accounts to reset');
      }
      loadTasks();
    } catch (error) {
      console.error('Error resetting spam accounts:', error);
      toast.error(error.response?.data?.error || 'Failed to reset spam accounts');
    }
  };

  const handleEditTask = (task) => {
    // Load task data into form
    setFormData({
      name: task.name,
      accountIds: task.accountIds.map(acc => acc._id || acc),
      recipientSource: task.recipientSource,
      manualRecipients: task.manualRecipients ? task.manualRecipients.join('\n') : '',
      scrapedDataTag: task.scrapedDataTag || '',
      scrapedDataSessionId: task.scrapedDataSessionId || '',
      messages: task.messages || [],
      settings: task.settings || {
        delayMin: 10,
        delayMax: 30,
        restInterval: 600,
        spamPauseDuration: 86400,
        messagesPerBatch: 15,
        batchPauseDuration: 600,
        maxRequestsPerDay: 350,
        sendMethod: 'rest'
      }
    });
    setBulkMessages('');
    setEditingTask(task);
    setShowEditModal(true);
  };

  const handleUpdateTask = async () => {
    try {
      // Validation
      if (!formData.name.trim()) {
        toast.error('Task name is required');
        return;
      }

      if (formData.accountIds.length === 0) {
        toast.error('Please select at least one account');
        return;
      }

      if (formData.messages.filter(m => m.trim()).length === 0) {
        toast.error('Please add at least one message');
        return;
      }

      let payload = {
        name: formData.name,
        accountIds: formData.accountIds,
        recipientSource: formData.recipientSource,
        messages: formData.messages.filter(m => m.trim()),
        settings: formData.settings
      };

      if (formData.recipientSource === 'manual') {
        const recipients = formData.manualRecipients
          .split('\n')
          .map(r => r.trim().replace('@', ''))
          .filter(r => r.length > 0);
        
        if (recipients.length === 0) {
          toast.error('Please add at least one recipient');
          return;
        }
        payload.manualRecipients = recipients;
      } else {
        if (!formData.scrapedDataTag) {
          toast.error('Please select scraped data source');
          return;
        }
        payload.scrapedDataTag = formData.scrapedDataTag;
        if (formData.scrapedDataSessionId) {
          payload.scrapedDataSessionId = formData.scrapedDataSessionId;
        }
      }

      const wasPaused = editingTask?.status === 'paused';
      
      await api.put(`/mass-dm/tasks/${editingTask._id}`, payload);
      toast.success('Task updated successfully');
      
      // If task was paused, resume it automatically
      if (wasPaused) {
        try {
          await api.post(`/mass-dm/tasks/${editingTask._id}/start`);
          toast.success('Task resumed from where it was paused');
        } catch (error) {
          console.error('Error resuming task:', error);
          toast.error('Task updated but failed to resume. Please start it manually.');
        }
      }
      
      setShowEditModal(false);
      setEditingTask(null);
      resetForm();
      loadTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error(error.response?.data?.error || 'Failed to update task');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      accountIds: [],
      recipientSource: 'manual',
      manualRecipients: '',
      scrapedDataTag: '',
      scrapedDataSessionId: '',
      messages: [],
      settings: {
        delayMin: 10,
        delayMax: 30,
        restInterval: 600,
        spamPauseDuration: 86400,
        messagesPerBatch: 15,
        batchPauseDuration: 600,
        maxRequestsPerDay: 350,
        sendMethod: 'rest'
      }
    });
    setBulkMessages('');
  };

  const handleSelectAllAccounts = () => {
    // Get accounts that are in the current task being edited (if any)
    // These should be preserved even if they show "In Use"
    const currentTaskAccountIds = editingTask 
      ? editingTask.accountIds.map(acc => acc._id || acc).map(id => id.toString())
      : [];
    
    // Only select accounts that are enabled (have valid session and not in use)
    // OR accounts that are in the current task being edited
    const enabledAccounts = accounts.filter(acc => {
      const hasValidSession = acc.hasValidSession === true;
      const inUse = acc.inUse === true;
      const isInCurrentTask = currentTaskAccountIds.includes(acc._id.toString());
      // Include if: (has valid session AND not in use) OR (is in current task being edited)
      return (hasValidSession && !inUse) || isInCurrentTask;
    });
    const enabledAccountIds = enabledAccounts.map(acc => acc._id);
    
    // Check if all enabled accounts (excluding current task accounts) are selected
    const selectableAccountIds = enabledAccounts
      .filter(acc => !currentTaskAccountIds.includes(acc._id.toString()))
      .map(acc => acc._id);
    const allSelectableSelected = selectableAccountIds.length > 0 && 
      selectableAccountIds.every(id => formData.accountIds.includes(id));
    
    if (allSelectableSelected) {
      // Deselect all selectable accounts, but keep accounts in current task
      setFormData({
        ...formData,
        accountIds: currentTaskAccountIds
      });
    } else {
      // Select all enabled accounts (including current task accounts)
      setFormData({
        ...formData,
        accountIds: enabledAccountIds
      });
    }
  };

  const handleToggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const handleSelectGroup = (groupName) => {
    const currentTaskAccountIds = editingTask 
      ? editingTask.accountIds.map(acc => acc._id || acc).map(id => id.toString())
      : [];
    
    const groupAccounts = accounts.filter(acc => {
      if (!acc.group || acc.group.trim() !== groupName.trim()) return false;
      const hasValidSession = acc.hasValidSession === true;
      const inUse = acc.inUse === true;
      const isInCurrentTask = currentTaskAccountIds.includes(acc._id.toString());
      return (hasValidSession && !inUse) || isInCurrentTask;
    });
    
    if (groupAccounts.length === 0) return;
    
    const groupAccountIds = groupAccounts.map(acc => acc._id);
    const allGroupSelected = groupAccountIds.every(id => formData.accountIds.includes(id));
    
    if (allGroupSelected) {
      // Deselect all accounts in group, but keep accounts in current task
      setFormData({
        ...formData,
        accountIds: formData.accountIds.filter(id => 
          !groupAccountIds.includes(id) || currentTaskAccountIds.includes(id.toString())
        )
      });
    } else {
      // Select all accounts in group
      setFormData({
        ...formData,
        accountIds: [...new Set([...formData.accountIds, ...groupAccountIds])]
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'stopped':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getProgressPercentage = (task) => {
    if (task.progress.total === 0) return 0;
    const completed = task.progress.sent + task.progress.failed;
    return Math.round((completed / task.progress.total) * 100);
  };

  // Memoized TaskCard component - only re-renders if its specific props change
  const TaskCard = memo(({ task, onStart, onStop, onPause, onEdit, onDelete, onResetSpamAccounts, getStatusColor, getProgressPercentage, isDropdownOpen, onToggleDropdown }) => {
    const taskId = task._id.toString();
    // Use ref to persist dropdown state across re-renders caused by socket updates
    const showDetailsRef = useRef(isDropdownOpen);
    const [showDetails, setShowDetails] = useState(isDropdownOpen);
    const [accountStats, setAccountStats] = useState({});
    const [loadingStats, setLoadingStats] = useState(false);
    const { socket } = useSocket();
    const statsRefetchTimeoutRef = useRef(null);

    // Sync ref and state with parent prop, but only update state if prop actually changed
    useEffect(() => {
      if (isDropdownOpen !== showDetailsRef.current) {
        showDetailsRef.current = isDropdownOpen;
        setShowDetails(isDropdownOpen);
      }
    }, [isDropdownOpen]);

    // Use ref value for display to prevent flickering
    const displayShowDetails = showDetailsRef.current || showDetails;

    // Fetch account stats when details are opened
    const fetchAccountStats = useCallback(() => {
      const hasAccountStatus = task.accountStatus && task.accountStatus.length > 0;
      const isOpen = showDetailsRef.current || showDetails;
      if (isOpen && task.status === 'running' && hasAccountStatus) {
        setLoadingStats(true);
        api.get(`/mass-dm/tasks/${task._id}/account-stats`)
          .then(res => {
            setAccountStats(res.data.accountStats || {});
            setLoadingStats(false);
          })
          .catch(err => {
            console.error('Error fetching account stats:', err);
            setLoadingStats(false);
          });
      }
    }, [showDetails, task._id, task.status, task.accountStatus]);

    useEffect(() => {
      fetchAccountStats();
    }, [fetchAccountStats]);

    // Listen for socket events to update account stats when dropdown is open
    useEffect(() => {
      const isOpen = showDetailsRef.current || showDetails;
      if (!socket || !isOpen || task.status !== 'running') return;

      const handleProgress = (data) => {
        // Only refetch if this event is for our task
        if (data.taskId === task._id.toString()) {
          // Clear any pending refetch
          if (statsRefetchTimeoutRef.current) {
            clearTimeout(statsRefetchTimeoutRef.current);
          }
          // Debounce refetch to avoid too many requests
          statsRefetchTimeoutRef.current = setTimeout(() => {
            fetchAccountStats();
          }, 1000); // Wait 1 second after last event before refetching
        }
      };

      const handleTaskUpdated = (data) => {
        // Only refetch if this event is for our task
        if (data.taskId === task._id.toString() && task.status === 'running') {
          // Clear any pending refetch
          if (statsRefetchTimeoutRef.current) {
            clearTimeout(statsRefetchTimeoutRef.current);
          }
          fetchAccountStats();
        }
      };

      socket.on('mass-dm-progress', handleProgress);
      socket.on('mass-dm-task-updated', handleTaskUpdated);

      return () => {
        socket.off('mass-dm-progress', handleProgress);
        socket.off('mass-dm-task-updated', handleTaskUpdated);
        if (statsRefetchTimeoutRef.current) {
          clearTimeout(statsRefetchTimeoutRef.current);
        }
      };
    }, [socket, showDetails, task._id, task.status, fetchAccountStats]);

    // Handle dropdown toggle - update both ref and state
    const handleToggle = () => {
      const newState = !showDetailsRef.current;
      showDetailsRef.current = newState;
      setShowDetails(newState);
      onToggleDropdown(taskId);
    };

    // Get account info helper
    const getAccountInfo = (accStatus) => {
      if (!task.accountIds || !accStatus) return null;
      
      const accountId = accStatus.accountId?._id || accStatus.accountId;
      const account = task.accountIds.find(acc => {
        const accId = acc._id?.toString() || acc.toString();
        return accId === accountId?.toString();
      });
      
      const accountIdStr = accountId?.toString();
      const stats = accountStats[accountIdStr] || { sent: 0, failed: 0, pending: 0, total: 0 };
      
      return {
        username: account?.username || 'Unknown',
        status: accStatus.status,
        accountStatus: account?.status || accStatus.accountStatus,
        sent: stats.sent,
        failed: stats.failed,
        pending: stats.pending,
        total: stats.total,
        dailySendCount: accStatus.dailySendCount || 0,
        dailySendResetAt: accStatus.dailySendResetAt,
        pausedUntil: accStatus.pausedUntil || account?.pauseUntil
      };
    };

    const getStatusBadgeColor = (status) => {
      if (status === 'active') return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      if (status === 'paused_spam') return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      if (status === 'paused_batch') return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      if (status === 'completed') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      if (status === 'failed') return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    };

    return (
      <div className="card">
        <div className="card-body">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {task.name}
                </h3>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(task.status)}`}>
                  {task.status}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <UserGroupIcon className="h-4 w-4" />
                  {task.accountIds.length} account{task.accountIds.length !== 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1">
                  <ChatBubbleLeftRightIcon className="h-4 w-4" />
                  {task.messages.length} message{task.messages.length !== 1 ? 's' : ''}
                </span>
                {task.recipientSource === 'scraped' && task.scrapedDataTag && (
                  <span>Tag: {task.scrapedDataTag}</span>
                )}
              </div>
              {task.status === 'running' && task.accountStatus && task.accountStatus.length > 0 && (
                <div className="mt-2 flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 flex-wrap">
                  <span className="flex items-center gap-1">
                    <UserGroupIcon className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                    {task.accountStatus.length} total
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircleIcon className="h-3 w-3 text-green-600" />
                    {task.accountStatus.filter(acc => {
                      // Account is active if: task status is 'active' AND Account.status is not 'daily_limit'
                      const isTaskActive = acc.status === 'active';
                      const isNotDailyLimit = !acc.accountStatus || acc.accountStatus !== 'daily_limit' || 
                                            (acc.accountPauseUntil && new Date(acc.accountPauseUntil) <= new Date());
                      return isTaskActive && isNotDailyLimit;
                    }).length} active
                  </span>
                  {/* Batch pause cooldown counter - accounts paused due to batch pause duration */}
                  <span className="flex items-center gap-1">
                    <ClockIcon className="h-3 w-3 text-purple-600" />
                    {task.accountStatus.filter(acc => {
                      // Account is in batch pause cooldown if status is paused_batch
                      // (matches what's shown in dropdown)
                      return acc.status === 'paused_batch';
                    }).length} cooldown
                  </span>
                  {/* Global Account.status counters - captcha, auth_expired, suspended */}
                  {task.accountStatus.filter(acc => acc.accountId?.status === 'captcha' || acc.status === 'captcha').length > 0 && (
                    <span className="flex items-center gap-1">
                      <XCircleIcon className="h-3 w-3 text-orange-600" />
                      {task.accountStatus.filter(acc => acc.accountId?.status === 'captcha' || acc.status === 'captcha').length} captcha
                    </span>
                  )}
                  {task.accountStatus.filter(acc => acc.accountId?.status === 'auth_expired' || acc.status === 'auth_expired').length > 0 && (
                    <span className="flex items-center gap-1">
                      <XCircleIcon className="h-3 w-3 text-orange-600" />
                      {task.accountStatus.filter(acc => acc.accountId?.status === 'auth_expired' || acc.status === 'auth_expired').length} auth expired
                    </span>
                  )}
                  {task.accountStatus.filter(acc => acc.accountId?.status === 'suspended' || acc.status === 'suspended').length > 0 && (
                    <span className="flex items-center gap-1">
                      <XCircleIcon className="h-3 w-3 text-red-600" />
                      {task.accountStatus.filter(acc => acc.accountId?.status === 'suspended' || acc.status === 'suspended').length} suspended
                    </span>
                  )}
                  {/* Daily limit counter - accounts paused due to daily request limit */}
                  {(() => {
                    const maxRequestsPerDay = task.settings?.maxRequestsPerDay;
                    if (!maxRequestsPerDay || maxRequestsPerDay <= 0) return null;
                    
                    const dailyLimitAccounts = task.accountStatus.filter(acc => {
                      // Account is paused due to daily limit if:
                      // - status is paused_spam
                      // - pausedUntil is in the future
                      // - dailySendCount >= maxRequestsPerDay (reached the limit)
                      const isPaused = acc.status === 'paused_spam';
                      const hasFuturePause = acc.pausedUntil && new Date(acc.pausedUntil) > new Date();
                      const reachedDailyLimit = acc.dailySendCount >= maxRequestsPerDay;
                      return isPaused && hasFuturePause && reachedDailyLimit;
                    });
                    
                    return dailyLimitAccounts.length > 0 ? (
                      <span className="flex items-center gap-1">
                        <ClockIcon className="h-3 w-3 text-yellow-600" />
                        {dailyLimitAccounts.length} daily limit
                      </span>
                    ) : null;
                  })()}
                  {/* Spam/rate limit counter - accounts paused due to spam detection (excludes daily limit accounts) */}
                  {(() => {
                    const maxRequestsPerDay = task.settings?.maxRequestsPerDay;
                    const spamLimitAccounts = task.accountStatus.filter(acc => {
                      const isPaused = acc.status === 'paused_spam';
                      const hasFuturePause = acc.pausedUntil && new Date(acc.pausedUntil) > new Date();
                      
                      // Exclude daily limit accounts from spam/rate limit count
                      if (maxRequestsPerDay && maxRequestsPerDay > 0) {
                        const reachedDailyLimit = acc.dailySendCount >= maxRequestsPerDay;
                        return isPaused && hasFuturePause && !reachedDailyLimit;
                      }
                      // If no daily limit set, all paused_spam accounts are spam/rate limit
                      return isPaused && hasFuturePause;
                    });
                    
                    return spamLimitAccounts.length > 0 ? (
                      <span className="flex items-center gap-1">
                        <XCircleIcon className="h-3 w-3 text-red-600" />
                        {spamLimitAccounts.length} spam/rate limit
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onResetSpamAccounts(task._id);
                          }}
                          className="ml-1 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 rounded transition-colors"
                          title="Reset spam/rate limited accounts to active"
                        >
                          Reset
                        </button>
                      </span>
                    ) : null;
                  })()}
                  {task.accountStatus.filter(acc => acc.status === 'completed').length > 0 && (
                    <span className="flex items-center gap-1">
                      <CheckCircleIcon className="h-3 w-3 text-blue-600" />
                      {task.accountStatus.filter(acc => acc.status === 'completed').length} completed
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {task.status === 'running' ? (
                <>
                  <button
                    onClick={() => onPause(task._id)}
                    className="p-2 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-xl transition-colors"
                    title="Pause task"
                  >
                    <PauseIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => onStop(task._id)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                    title="Stop task"
                  >
                    <StopIcon className="h-5 w-5" />
                  </button>
                </>
              ) : task.status === 'paused' || task.status === 'failed' ? (
                <>
                  <button
                    onClick={() => onStart(task._id)}
                    className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-xl transition-colors"
                    title={task.status === 'failed' ? 'Retry failed task' : 'Restart task'}
                  >
                    <PlayIcon className="h-5 w-5" />
                  </button>
                  {task.status !== 'failed' && (
                    <button
                      onClick={() => onStop(task._id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                      title="Stop task"
                    >
                      <StopIcon className="h-5 w-5" />
                    </button>
                  )}
                </>
              ) : task.status === 'pending' || task.status === 'stopped' ? (
                <button
                  onClick={() => onStart(task._id)}
                  className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors"
                  title="Start task"
                >
                  <PlayIcon className="h-5 w-5" />
                </button>
              ) : task.status === 'completed' && task.progress.pending > 0 ? (
                <button
                  onClick={() => onStart(task._id)}
                  className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors"
                  title="Restart task (continue with pending recipients)"
                >
                  <PlayIcon className="h-5 w-5" />
                </button>
              ) : null}
              {(task.status === 'pending' || task.status === 'stopped' || task.status === 'paused' || task.status === 'failed') && (
                <button
                  onClick={() => onEdit(task)}
                  className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-colors"
                  title="Edit task"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={() => onDelete(task._id)}
                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                title="Delete task"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          {task.status === 'running' || task.status === 'completed' || task.status === 'paused' ? (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Progress: {task.progress.sent + task.progress.failed} / {task.progress.total}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {getProgressPercentage(task)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-primary-600 transition-all duration-300"
                  style={{ width: `${getProgressPercentage(task)}%` }}
                />
              </div>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <CheckCircleIcon className="h-3 w-3 text-green-600" />
                  {task.progress.sent} sent
                </span>
                <span className="flex items-center gap-1">
                  <XCircleIcon className="h-3 w-3 text-red-600" />
                  {task.progress.failed} failed
                </span>
                <span className="flex items-center gap-1">
                  <ClockIcon className="h-3 w-3 text-yellow-600" />
                  {task.progress.pending} pending
                </span>
              </div>
            </div>
          ) : null}

          {/* Account Details Section */}
          {task.accountStatus && task.accountStatus.length > 0 && (
            <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
              <button
                onClick={handleToggle}
                className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <span>Account Details</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ({task.accountStatus.filter(acc => acc.status !== 'auth_expired').length} accounts)
                  </span>
                </span>
                {displayShowDetails ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform" />
                )}
              </button>

              {/* Collapsible Details Section with smooth slide */}
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  displayShowDetails ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0'
                }`}
              >
                {loadingStats ? (
                  <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                    Loading account stats...
                  </div>
                ) : (
                  <div className="max-h-[800px] overflow-y-auto pr-2 space-y-2 scroll-smooth">
                    {task.accountStatus
                      .filter(accStatus => accStatus.status !== 'auth_expired') // Filter out auth_expired accounts
                      .map((accStatus, index) => {
                      const accountInfo = getAccountInfo(accStatus);
                      if (!accountInfo) return null;

                      const maxRequestsPerDay = task.settings?.maxRequestsPerDay;
                      const isDailyLimit = maxRequestsPerDay && 
                        accStatus.dailySendCount >= maxRequestsPerDay && 
                        accStatus.status === 'paused_spam';

                      return (
                        <div
                          key={index}
                          className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              @{accountInfo.username}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadgeColor(accountInfo.status)}`}>
                              {accountInfo.status === 'paused_spam' && isDailyLimit 
                                ? 'Daily Limit' 
                                : accountInfo.status === 'paused_batch' 
                                ? 'cooldown' 
                                : accountInfo.status}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Sent:</span>
                              <span className="ml-1 font-medium text-green-600 dark:text-green-400">
                                {accountInfo.sent}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Failed:</span>
                              <span className="ml-1 font-medium text-red-600 dark:text-red-400">
                                {accountInfo.failed}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Recipients:</span>
                              <span className="ml-1 font-medium text-gray-900 dark:text-gray-100">
                                {accountInfo.total}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Progress:</span>
                              <span className="ml-1 font-medium text-gray-900 dark:text-gray-100">
                                {accountInfo.total > 0 
                                  ? Math.round(((accountInfo.sent + accountInfo.failed) / accountInfo.total) * 100)
                                  : 0}%
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Daily:</span>
                              <span className="ml-1 font-medium text-gray-900 dark:text-gray-100">
                                {accountInfo.dailySendCount}
                                {maxRequestsPerDay && ` / ${maxRequestsPerDay}`}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }, (prevProps, nextProps) => {
    // Custom comparison - only re-render if these specific fields change
    const prev = prevProps.task;
    const next = nextProps.task;
    
    // Check if accountStatus changed (for dailySendCount updates)
    // Compare by accountId to handle array reordering and be more stable
    const prevAccountStatus = prev.accountStatus || [];
    const nextAccountStatus = next.accountStatus || [];
    
    let accountStatusChanged = false;
    if (prevAccountStatus.length !== nextAccountStatus.length) {
      accountStatusChanged = true;
    } else {
      // Create maps by accountId for stable comparison (not index-based)
      const prevMap = new Map();
      const nextMap = new Map();
      
      prevAccountStatus.forEach(acc => {
        const id = acc.accountId?.toString() || acc.accountId;
        if (id) prevMap.set(id, acc);
      });
      
      nextAccountStatus.forEach(acc => {
        const id = acc.accountId?.toString() || acc.accountId;
        if (id) nextMap.set(id, acc);
      });
      
      // Check if any account status changed
      for (const [id, prevAcc] of prevMap.entries()) {
        const nextAcc = nextMap.get(id);
        if (!nextAcc) {
          accountStatusChanged = true;
          break;
        }
        
        // Compare relevant fields with proper null/undefined handling
        const prevPausedUntil = prevAcc.pausedUntil instanceof Date ? prevAcc.pausedUntil.getTime() : null;
        const nextPausedUntil = nextAcc.pausedUntil instanceof Date ? nextAcc.pausedUntil.getTime() : null;
        
        if (
          prevAcc.status !== nextAcc.status ||
          prevAcc.dailySendCount !== nextAcc.dailySendCount ||
          prevPausedUntil !== nextPausedUntil
        ) {
          accountStatusChanged = true;
          break;
        }
      }
      
      // Check if any new accounts were added
      if (!accountStatusChanged && nextMap.size !== prevMap.size) {
        accountStatusChanged = true;
      }
    }
    
    // Also check if dropdown state changed
    const prevIsOpen = prevProps.isDropdownOpen;
    const nextIsOpen = nextProps.isDropdownOpen;
    
    return (
      prev._id === next._id &&
      prev.status === next.status &&
      prev.name === next.name &&
      prev.progress?.sent === next.progress?.sent &&
      prev.progress?.failed === next.progress?.failed &&
      prev.progress?.pending === next.progress?.pending &&
      prev.progress?.total === next.progress?.total &&
      prev.accountIds?.length === next.accountIds?.length &&
      prev.messages?.length === next.messages?.length &&
      prevIsOpen === nextIsOpen &&
      !accountStatusChanged
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner h-8 w-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className={`relative overflow-hidden rounded-2xl ${
        isDarkMode 
          ? 'bg-gradient-to-tl from-slate-900 via-blue-950 to-slate-900' 
          : 'bg-gradient-to-tl from-sky-50 via-blue-100 to-sky-100'
      }`}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient orbs */}
          <div className={`absolute -bottom-16 -left-16 w-72 h-72 rounded-full blur-3xl ${
            isDarkMode ? 'bg-blue-600/20' : 'bg-blue-300/40'
          }`} />
          <div className={`absolute -top-20 right-1/3 w-80 h-80 rounded-full blur-3xl ${
            isDarkMode ? 'bg-sky-600/15' : 'bg-sky-300/30'
          }`} />
          <div className={`absolute bottom-1/3 -right-10 w-56 h-56 rounded-full blur-3xl ${
            isDarkMode ? 'bg-cyan-600/10' : 'bg-cyan-200/30'
          }`} />
          
          {/* Grid pattern overlay */}
          <div 
            className={`absolute inset-0 ${isDarkMode ? 'opacity-[0.03]' : 'opacity-[0.08]'}`}
            style={{
              backgroundImage: `linear-gradient(${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
          
          {/* Floating decorative shapes */}
          <svg className="absolute top-8 right-12 w-18 h-18 opacity-10" viewBox="0 0 100 100">
            <path d="M20,50 L50,20 L80,50 L50,80 Z" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="2" />
            <path d="M35,50 L50,35 L65,50 L50,65 Z" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="1.5" />
          </svg>
          <svg className="absolute bottom-10 left-1/4 w-14 h-14 opacity-10" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="35" fill="none" stroke={isDarkMode ? '#7dd3fc' : '#0284c7'} strokeWidth="2" />
          </svg>
          <svg className="absolute top-1/2 left-10 w-12 h-12 opacity-10" viewBox="0 0 100 100">
            <rect x="25" y="25" width="50" height="50" fill="none" stroke={isDarkMode ? '#67e8f9' : '#0891b2'} strokeWidth="2" transform="rotate(45 50 50)" />
          </svg>
        </div>
        
        {/* Content */}
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {/* Icon/Logo area */}
              <div className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-blue-500 to-sky-600 shadow-lg shadow-blue-500/30' 
                  : 'bg-gradient-to-br from-blue-500 to-sky-500 shadow-lg shadow-blue-400/40'
              }`}>
                <PaperAirplaneIcon className="w-8 h-8 text-white" />
                <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-cyan-400' : 'bg-cyan-500'
                }`}>
                  <SparklesIcon className="w-3 h-3 text-white" />
                </div>
              </div>
              
              {/* Text content */}
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Mass DMs
                </h1>
                <p className={`mt-1.5 text-base ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Manage and monitor mass DM campaigns
                </p>
                <div className={`mt-3 flex items-center space-x-4 text-sm ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      isDarkMode ? 'bg-emerald-400' : 'bg-emerald-500'
                    }`} />
                    {tasks.filter(t => t.status === 'running').length} Running
                  </span>
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      isDarkMode ? 'bg-slate-500' : 'bg-slate-400'
                    }`} />
                    {tasks.length} Task{tasks.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  if (user?.role !== 'admin' && !user?.features?.canUseMassDM) {
                    setShowPermissionModal(true);
                    return;
                  }
                  setShowCreateModal(true);
                }}
                className={`inline-flex items-center px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40' 
                    : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                }`}
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Task
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tasks List */}
      <div className="space-y-4">
        {tasks.length === 0 ? (
          <div className="card text-center py-12">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No tasks yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">Create your first mass DM task to get started</p>
            <button
              onClick={() => {
                // Check if user has permission
                if (user?.role !== 'admin' && !user?.features?.canUseMassDM) {
                  setShowPermissionModal(true);
                  return;
                }
                setShowCreateModal(true);
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Create Task
            </button>
          </div>
        ) : (
          tasks.map((task) => (
            <TaskCard
              key={task._id}
              task={task}
              onStart={handleStartTask}
              onStop={handleStopTask}
              onPause={handlePauseTask}
              onEdit={handleEditTask}
              onDelete={handleDeleteTask}
              onResetSpamAccounts={handleResetSpamAccounts}
              getStatusColor={getStatusColor}
              getProgressPercentage={getProgressPercentage}
              isDropdownOpen={openDropdowns.has(task._id.toString())}
              onToggleDropdown={handleToggleDropdown}
            />
          ))
        )}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Create Mass DM Task</h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Task Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Task Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="e.g., Campaign 1 - November 2024"
                  />
                </div>

                {/* Account Selection */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Select Accounts *
                    </label>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {formData.accountIds.length} of {accounts.filter(acc => acc.hasValidSession === true && acc.inUse !== true).length} selected
                    </span>
                  </div>
                  <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3">
                    {accounts.length > 0 && (
                      <label className="flex items-center gap-2 cursor-pointer pb-2 border-b border-gray-200 dark:border-gray-700 mb-2">
                        <input
                          type="checkbox"
                          checked={accounts.filter(acc => acc.hasValidSession === true && acc.inUse !== true).length > 0 && 
                                   accounts.filter(acc => acc.hasValidSession === true && acc.inUse !== true)
                                     .every(acc => formData.accountIds.includes(acc._id))}
                          onChange={handleSelectAllAccounts}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Select All Available
                        </span>
                      </label>
                    )}
                    {accounts.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 p-2">
                        No accounts found. Please add accounts first.
                      </p>
                    ) : (
                      <>
                        {/* Groups Section */}
                        {(() => {
                          // Get unique groups from accounts (only show groups that have accounts in the current list)
                          const accountGroups = [...new Set(accounts
                            .map(acc => acc.group)
                            .filter(g => g != null && String(g).trim() !== '')
                            .map(g => String(g).trim()))];
                          
                          if (accountGroups.length === 0) return null;
                          
                          return (
                            <div className="space-y-1 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                Groups
                              </div>
                              {accountGroups.sort().map((groupName) => {
                                const groupAccounts = accounts.filter(acc => {
                                  if (!acc.group) return false;
                                  return String(acc.group).trim() === groupName;
                                });
                                const selectableGroupAccounts = groupAccounts.filter(acc => 
                                  acc.hasValidSession === true && acc.inUse !== true
                                );
                                const groupAccountIds = selectableGroupAccounts.map(acc => acc._id);
                                const allGroupSelected = selectableGroupAccounts.length > 0 && 
                                  groupAccountIds.every(id => formData.accountIds.includes(id));
                                const isExpanded = expandedGroups.has(groupName);
                                
                                return (
                                  <div key={groupName} className="space-y-1">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center flex-1 gap-2">
                                        <button
                                          type="button"
                                          onClick={() => handleToggleGroup(groupName)}
                                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                        >
                                          {isExpanded ? (
                                            <ChevronDownIcon className="h-4 w-4" />
                                          ) : (
                                            <ChevronDownIcon className="h-4 w-4 transform -rotate-90" />
                                          )}
                                        </button>
                                        <input
                                          type="checkbox"
                                          checked={allGroupSelected}
                                          onChange={() => handleSelectGroup(groupName)}
                                          disabled={selectableGroupAccounts.length === 0}
                                          className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                                            selectableGroupAccounts.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                          }`}
                                        />
                                        <span className={`text-sm font-medium ${
                                          selectableGroupAccounts.length === 0
                                            ? 'text-gray-400 dark:text-gray-500'
                                            : 'text-gray-700 dark:text-gray-300'
                                        }`}>
                                          {groupName} ({selectableGroupAccounts.length}/{groupAccounts.length})
                                        </span>
                                      </div>
                                    </div>
                                    {isExpanded && (
                                      <div className="ml-6 space-y-1 mt-1">
                                        {groupAccounts.map((account) => {
                                          const hasValidSession = account.hasValidSession === true;
                                          const inUse = account.inUse === true;
                                          const isDisabled = !hasValidSession || inUse;
                                          return (
                                            <label key={account._id} className="flex items-center justify-between">
                                              <div className="flex items-center flex-1">
                                                <input
                                                  type="checkbox"
                                                  checked={formData.accountIds.includes(account._id)}
                                                  onChange={(e) => {
                                                    if (e.target.checked) {
                                                      setFormData({
                                                        ...formData,
                                                        accountIds: [...formData.accountIds, account._id]
                                                      });
                                                    } else {
                                                      setFormData({
                                                        ...formData,
                                                        accountIds: formData.accountIds.filter(id => id !== account._id)
                                                      });
                                                    }
                                                  }}
                                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                  disabled={isDisabled}
                                                />
                                                <span className={`ml-2 text-sm ${
                                                  isDisabled 
                                                    ? 'text-gray-400 dark:text-gray-500' 
                                                    : 'text-gray-700 dark:text-gray-300'
                                                }`}>
                                                  {account.displayName || account.username}
                                                </span>
                                              </div>
                                              {!hasValidSession && (
                                                <span className="ml-2 text-xs text-red-600 dark:text-red-400" title="Run Login Test for this account">
                                                  ⚠️ No Session
                                                </span>
                                              )}
                                              {hasValidSession && inUse && (
                                                <span className="ml-2 text-xs text-orange-600 dark:text-orange-400" title="Account is currently being used in an active mass DM task">
                                                  ⚠️ In Use
                                                </span>
                                              )}
                                              {hasValidSession && !inUse && (
                                                <span className="ml-2 text-xs text-green-600 dark:text-green-400" title="Account has valid session data">
                                                  ✓ Ready
                                                </span>
                                              )}
                                            </label>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                        
                        {/* Ungrouped Accounts */}
                        {(() => {
                          const ungroupedAccounts = accounts.filter(acc => !acc.group || acc.group.trim() === '');
                          if (ungroupedAccounts.length === 0) return null;
                          
                          return (
                            <div className="space-y-1">
                              {(() => {
                                const accountGroups = [...new Set(accounts
                                  .map(acc => acc.group)
                                  .filter(g => g != null && String(g).trim() !== ''))];
                                if (accountGroups.length > 0) {
                                  return (
                                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                      Ungrouped
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              {ungroupedAccounts.map((account) => {
                                const hasValidSession = account.hasValidSession === true;
                                const inUse = account.inUse === true;
                                const isDisabled = !hasValidSession || inUse;
                                return (
                                  <label key={account._id} className="flex items-center justify-between">
                                    <div className="flex items-center flex-1">
                                      <input
                                        type="checkbox"
                                        checked={formData.accountIds.includes(account._id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setFormData({
                                              ...formData,
                                              accountIds: [...formData.accountIds, account._id]
                                            });
                                          } else {
                                            setFormData({
                                              ...formData,
                                              accountIds: formData.accountIds.filter(id => id !== account._id)
                                            });
                                          }
                                        }}
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        disabled={isDisabled}
                                      />
                                      <span className={`ml-2 text-sm ${
                                        isDisabled 
                                          ? 'text-gray-400 dark:text-gray-500' 
                                          : 'text-gray-700 dark:text-gray-300'
                                      }`}>
                                        {account.displayName || account.username}
                                      </span>
                                    </div>
                                    {!hasValidSession && (
                                      <span className="ml-2 text-xs text-red-600 dark:text-red-400" title="Run Login Test for this account">
                                        ⚠️ No Session
                                      </span>
                                    )}
                                    {hasValidSession && inUse && (
                                      <span className="ml-2 text-xs text-orange-600 dark:text-orange-400" title="Account is currently being used in an active mass DM task">
                                        ⚠️ In Use
                                      </span>
                                    )}
                                    {hasValidSession && !inUse && (
                                      <span className="ml-2 text-xs text-green-600 dark:text-green-400" title="Account has valid session data">
                                        ✓ Ready
                                      </span>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>

                {/* Recipient Source */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Recipient Source *
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="manual"
                        checked={formData.recipientSource === 'manual'}
                        onChange={(e) => setFormData({ ...formData, recipientSource: e.target.value })}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Paste Usernames</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="scraped"
                        checked={formData.recipientSource === 'scraped'}
                        onChange={(e) => setFormData({ ...formData, recipientSource: e.target.value })}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Scraped Data</span>
                    </label>
                  </div>
                </div>

                {/* Manual Recipients */}
                {formData.recipientSource === 'manual' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Recipients (one per line, @ optional) *
                    </label>
                    <textarea
                      value={formData.manualRecipients}
                      onChange={(e) => setFormData({ ...formData, manualRecipients: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                      rows="6"
                      placeholder="username1&#10;username2&#10;@username3"
                    />
                  </div>
                )}

                {/* Scraped Data Selection */}
                {formData.recipientSource === 'scraped' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Select Scraped Data *
                    </label>
                    <select
                      value={formData.scrapedDataTag}
                      onChange={(e) => setFormData({ ...formData, scrapedDataTag: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">-- Select a tag --</option>
                      {scrapedData.tags.map((tag) => (
                        <option key={tag.tag} value={tag.tag}>
                          {tag.tag} - {tag.canDmCount} can DM (scraped {new Date(tag.latestScrape).toLocaleDateString()})
                        </option>
                      ))}
                    </select>
                    {scrapedData.sessions.length > 0 && (
                      <select
                        value={formData.scrapedDataSessionId}
                        onChange={(e) => setFormData({ ...formData, scrapedDataSessionId: e.target.value })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">-- Optional: Select specific session --</option>
                        {scrapedData.sessions.map((session) => (
                          <option key={session.sessionId} value={session.sessionId}>
                            {session.name} ({session.tag})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Messages */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      DM Message(s) * (supports emoji and formatting)
                    </label>
                    {formData.messages.filter(m => m.trim()).length > 0 && (
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {formData.messages.filter(m => m.trim()).length} message{formData.messages.filter(m => m.trim()).length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  
                  {/* Bulk Load Messages */}
                  <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Bulk Load Messages (one per line)
                      </label>
                      <button
                        onClick={() => {
                          const lines = bulkMessages
                            .split('\n')
                            .map(line => line.trim())
                            .filter(line => line.length > 0);
                          if (lines.length > 0) {
                            setFormData({ ...formData, messages: lines });
                            setBulkMessages('');
                            toast.success(`Loaded ${lines.length} message${lines.length !== 1 ? 's' : ''}`);
                          } else {
                            toast.error('Please enter at least one message');
                          }
                        }}
                        className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                      >
                        Load Messages
                      </button>
                    </div>
                    <textarea
                      value={bulkMessages}
                      onChange={(e) => setBulkMessages(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                      rows="6"
                      placeholder="Message 1&#10;Message 2&#10;Message 3"
                    />
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Paste multiple messages, one per line. Click "Load Messages" to add them all at once.
                    </p>
                  </div>

                  {/* Show messages summary if loaded */}
                  {formData.messages.filter(m => m.trim()).length > 0 && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-md border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {formData.messages.filter(m => m.trim()).length} message{formData.messages.filter(m => m.trim()).length !== 1 ? 's' : ''} loaded
                        </span>
                        <button
                          onClick={() => {
                            setFormData({ ...formData, messages: [] });
                            setBulkMessages('');
                          }}
                          className="text-xs text-red-600 hover:text-red-700 font-medium"
                        >
                          Clear All
                        </button>
                      </div>
                      <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Example (Message 1):</p>
                        <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {formData.messages.find(m => m.trim()) || ''}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Settings */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Settings</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Delay Min (seconds)
                      </label>
                      <input
                        type="number"
                        value={formData.settings.delayMin}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, delayMin: parseInt(e.target.value) || 10 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Delay Max (seconds)
                      </label>
                      <input
                        type="number"
                        value={formData.settings.delayMax}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, delayMax: parseInt(e.target.value) || 30 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Rest Interval (seconds)
                      </label>
                      <input
                        type="number"
                        value={formData.settings.restInterval}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, restInterval: parseInt(e.target.value) || 600 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Spam Pause Duration (seconds)
                      </label>
                      <input
                        type="number"
                        value={formData.settings.spamPauseDuration}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, spamPauseDuration: parseInt(e.target.value) || 86400 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Messages Per Batch
                      </label>
                      <input
                        type="number"
                        value={formData.settings.messagesPerBatch}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, messagesPerBatch: parseInt(e.target.value) || 15 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="1"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Number of DMs to send before pausing</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Batch Pause Duration (seconds)
                      </label>
                      <input
                        type="number"
                        value={formData.settings.batchPauseDuration}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, batchPauseDuration: parseInt(e.target.value) || 600 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="0"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Pause duration after each batch</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Max Requests Per Day (Per Account)
                      </label>
                      <input
                        type="number"
                        value={formData.settings.maxRequestsPerDay || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, maxRequestsPerDay: e.target.value ? parseInt(e.target.value) : null }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="1"
                        placeholder="Leave empty for unlimited"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Maximum requests per account per day (includes failed sends). Leave empty for unlimited. Resets every 24 hours.</p>
                    </div>
                    {/* Send Method Selection - Hidden, always uses REST */}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
            <button
              onClick={handleCreateTask}
              className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
            >
              Create Task
            </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditModal && editingTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit Mass DM Task</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTask(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Task Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Task Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="e.g., Campaign 1 - November 2024"
                  />
                </div>

                {/* Account Selection */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Select Accounts *
                    </label>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {formData.accountIds.length} of {accounts.filter(acc => acc.hasValidSession === true && acc.inUse !== true).length} selected
                    </span>
                  </div>
                  <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3">
                    {accounts.length > 0 && (() => {
                      // Get accounts that are in the current task being edited (if any)
                      const currentTaskAccountIds = editingTask 
                        ? editingTask.accountIds.map(acc => acc._id || acc).map(id => id.toString())
                        : [];
                      
                      // Get selectable accounts (excluding accounts already in current task)
                      const selectableAccounts = accounts.filter(acc => {
                        const hasValidSession = acc.hasValidSession === true;
                        const inUse = acc.inUse === true;
                        const isInCurrentTask = currentTaskAccountIds.includes(acc._id.toString());
                        // Only include if: has valid session AND not in use AND not in current task
                        return hasValidSession && !inUse && !isInCurrentTask;
                      });
                      
                      const allSelectableSelected = selectableAccounts.length > 0 && 
                        selectableAccounts.every(acc => formData.accountIds.includes(acc._id));
                      
                      return (
                        <label className="flex items-center gap-2 cursor-pointer pb-2 border-b border-gray-200 dark:border-gray-700 mb-2">
                          <input
                            type="checkbox"
                            checked={allSelectableSelected}
                            onChange={handleSelectAllAccounts}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Select All Available
                          </span>
                        </label>
                      );
                    })()}
                    {accounts.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 p-2">
                        No accounts found. Please add accounts first.
                      </p>
                    ) : (
                      <>
                        {/* Groups Section */}
                        {(() => {
                          // Get unique groups from accounts (only show groups that have accounts in the current list)
                          const accountGroups = [...new Set(accounts
                            .map(acc => acc.group)
                            .filter(g => g != null && String(g).trim() !== '')
                            .map(g => String(g).trim()))];
                          
                          if (accountGroups.length === 0) return null;
                          
                          const currentTaskAccountIds = editingTask 
                            ? editingTask.accountIds.map(acc => acc._id || acc).map(id => id.toString())
                            : [];
                          
                          return (
                            <div className="space-y-1 mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                Groups
                              </div>
                              {accountGroups.sort().map((groupName) => {
                                const groupAccounts = accounts.filter(acc => {
                                  if (!acc.group) return false;
                                  return String(acc.group).trim() === groupName;
                                });
                                const selectableGroupAccounts = groupAccounts.filter(acc => {
                                  const hasValidSession = acc.hasValidSession === true;
                                  const inUse = acc.inUse === true;
                                  const isInCurrentTask = currentTaskAccountIds.includes(acc._id.toString());
                                  return (hasValidSession && !inUse && !isInCurrentTask) || isInCurrentTask;
                                });
                                const groupAccountIds = selectableGroupAccounts.map(acc => acc._id);
                                const allGroupSelected = selectableGroupAccounts.length > 0 && 
                                  groupAccountIds.every(id => formData.accountIds.includes(id));
                                const isExpanded = expandedGroups.has(groupName);
                                
                                return (
                                  <div key={groupName} className="space-y-1">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center flex-1 gap-2">
                                        <button
                                          type="button"
                                          onClick={() => handleToggleGroup(groupName)}
                                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                        >
                                          {isExpanded ? (
                                            <ChevronDownIcon className="h-4 w-4" />
                                          ) : (
                                            <ChevronDownIcon className="h-4 w-4 transform -rotate-90" />
                                          )}
                                        </button>
                                        <input
                                          type="checkbox"
                                          checked={allGroupSelected}
                                          onChange={() => handleSelectGroup(groupName)}
                                          disabled={selectableGroupAccounts.length === 0}
                                          className={`rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                                            selectableGroupAccounts.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                          }`}
                                        />
                                        <span className={`text-sm font-medium ${
                                          selectableGroupAccounts.length === 0
                                            ? 'text-gray-400 dark:text-gray-500'
                                            : 'text-gray-700 dark:text-gray-300'
                                        }`}>
                                          {groupName} ({selectableGroupAccounts.length}/{groupAccounts.length})
                                        </span>
                                      </div>
                                    </div>
                                    {isExpanded && (
                                      <div className="ml-6 space-y-1 mt-1">
                                        {groupAccounts.map((account) => {
                                          const hasValidSession = account.hasValidSession === true;
                                          const inUse = account.inUse === true;
                                          const isInCurrentTask = editingTask && editingTask.accountIds
                                            .map(acc => acc._id || acc)
                                            .map(id => id.toString())
                                            .includes(account._id.toString());
                                          const isDisabled = !hasValidSession || (inUse && !isInCurrentTask);
                                          return (
                                            <label key={account._id} className="flex items-center justify-between">
                                              <div className="flex items-center flex-1">
                                                <input
                                                  type="checkbox"
                                                  checked={formData.accountIds.includes(account._id)}
                                                  onChange={(e) => {
                                                    if (e.target.checked) {
                                                      setFormData({
                                                        ...formData,
                                                        accountIds: [...formData.accountIds, account._id]
                                                      });
                                                    } else {
                                                      setFormData({
                                                        ...formData,
                                                        accountIds: formData.accountIds.filter(id => id !== account._id)
                                                      });
                                                    }
                                                  }}
                                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                  disabled={isDisabled}
                                                />
                                                <span className={`ml-2 text-sm ${
                                                  isDisabled 
                                                    ? 'text-gray-400 dark:text-gray-500' 
                                                    : 'text-gray-700 dark:text-gray-300'
                                                }`}>
                                                  {account.displayName || account.username}
                                                </span>
                                              </div>
                                              {!hasValidSession && (
                                                <span className="ml-2 text-xs text-red-600 dark:text-red-400" title="Run Login Test for this account">
                                                  ⚠️ No Session
                                                </span>
                                              )}
                                              {hasValidSession && inUse && (
                                                <span className="ml-2 text-xs text-orange-600 dark:text-orange-400" title="Account is currently being used in an active mass DM task">
                                                  ⚠️ In Use
                                                </span>
                                              )}
                                              {hasValidSession && !inUse && (
                                                <span className="ml-2 text-xs text-green-600 dark:text-green-400" title="Account has valid session data">
                                                  ✓ Ready
                                                </span>
                                              )}
                                            </label>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                        
                        {/* Ungrouped Accounts */}
                        {(() => {
                          const ungroupedAccounts = accounts.filter(acc => !acc.group || acc.group.trim() === '');
                          if (ungroupedAccounts.length === 0) return null;
                          
                          return (
                            <div className="space-y-1">
                              {(() => {
                                const accountGroups = [...new Set(accounts
                                  .map(acc => acc.group)
                                  .filter(g => g != null && String(g).trim() !== ''))];
                                if (accountGroups.length > 0) {
                                  return (
                                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                      Ungrouped
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              {ungroupedAccounts.map((account) => {
                                const hasValidSession = account.hasValidSession === true;
                                const inUse = account.inUse === true;
                                const isInCurrentTask = editingTask && editingTask.accountIds
                                  .map(acc => acc._id || acc)
                                  .map(id => id.toString())
                                  .includes(account._id.toString());
                                const isDisabled = !hasValidSession || (inUse && !isInCurrentTask);
                                return (
                                  <label key={account._id} className="flex items-center justify-between">
                                    <div className="flex items-center flex-1">
                                      <input
                                        type="checkbox"
                                        checked={formData.accountIds.includes(account._id)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setFormData({
                                              ...formData,
                                              accountIds: [...formData.accountIds, account._id]
                                            });
                                          } else {
                                            setFormData({
                                              ...formData,
                                              accountIds: formData.accountIds.filter(id => id !== account._id)
                                            });
                                          }
                                        }}
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        disabled={isDisabled}
                                      />
                                      <span className={`ml-2 text-sm ${
                                        isDisabled 
                                          ? 'text-gray-400 dark:text-gray-500' 
                                          : 'text-gray-700 dark:text-gray-300'
                                      }`}>
                                        {account.displayName || account.username}
                                      </span>
                                    </div>
                                    {!hasValidSession && (
                                      <span className="ml-2 text-xs text-red-600 dark:text-red-400" title="Run Login Test for this account">
                                        ⚠️ No Session
                                      </span>
                                    )}
                                    {hasValidSession && inUse && (
                                      <span className="ml-2 text-xs text-orange-600 dark:text-orange-400" title="Account is currently being used in an active mass DM task">
                                        ⚠️ In Use
                                      </span>
                                    )}
                                    {hasValidSession && !inUse && (
                                      <span className="ml-2 text-xs text-green-600 dark:text-green-400" title="Account has valid session data">
                                        ✓ Ready
                                      </span>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>

                {/* Recipient Source */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Recipient Source *
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="manual"
                        checked={formData.recipientSource === 'manual'}
                        onChange={(e) => setFormData({ ...formData, recipientSource: e.target.value })}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Paste Usernames</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="scraped"
                        checked={formData.recipientSource === 'scraped'}
                        onChange={(e) => setFormData({ ...formData, recipientSource: e.target.value })}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Scraped Data</span>
                    </label>
                  </div>
                </div>

                {/* Manual Recipients */}
                {formData.recipientSource === 'manual' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Recipients (one per line, @ optional) *
                    </label>
                    <textarea
                      value={formData.manualRecipients}
                      onChange={(e) => setFormData({ ...formData, manualRecipients: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                      rows="6"
                      placeholder="username1&#10;username2&#10;@username3"
                    />
                  </div>
                )}

                {/* Scraped Data Selection */}
                {formData.recipientSource === 'scraped' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Select Scraped Data *
                    </label>
                    <select
                      value={formData.scrapedDataTag}
                      onChange={(e) => setFormData({ ...formData, scrapedDataTag: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">-- Select a tag --</option>
                      {scrapedData.tags.map((tag) => (
                        <option key={tag.tag} value={tag.tag}>
                          {tag.tag} - {tag.canDmCount} can DM (scraped {new Date(tag.latestScrape).toLocaleDateString()})
                        </option>
                      ))}
                    </select>
                    {scrapedData.sessions.length > 0 && (
                      <select
                        value={formData.scrapedDataSessionId}
                        onChange={(e) => setFormData({ ...formData, scrapedDataSessionId: e.target.value })}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="">-- Optional: Select specific session --</option>
                        {scrapedData.sessions.map((session) => (
                          <option key={session.sessionId} value={session.sessionId}>
                            {session.name} ({session.tag})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {/* Messages */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      DM Message(s) * (supports emoji and formatting)
                    </label>
                    {formData.messages.filter(m => m.trim()).length > 0 && (
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {formData.messages.filter(m => m.trim()).length} message{formData.messages.filter(m => m.trim()).length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  
                  {/* Bulk Load Messages */}
                  <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Bulk Load Messages (one per line)
                      </label>
                      <button
                        onClick={() => {
                          const lines = bulkMessages
                            .split('\n')
                            .map(line => line.trim())
                            .filter(line => line.length > 0);
                          if (lines.length > 0) {
                            setFormData({ ...formData, messages: lines });
                            setBulkMessages('');
                            toast.success(`Loaded ${lines.length} message${lines.length !== 1 ? 's' : ''}`);
                          } else {
                            toast.error('Please enter at least one message');
                          }
                        }}
                        className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                      >
                        Load Messages
                      </button>
                    </div>
                    <textarea
                      value={bulkMessages}
                      onChange={(e) => setBulkMessages(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                      rows="6"
                      placeholder="Message 1&#10;Message 2&#10;Message 3"
                    />
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Paste multiple messages, one per line. Click "Load Messages" to add them all at once.
                    </p>
                  </div>

                  {/* Show messages summary if loaded */}
                  {formData.messages.filter(m => m.trim()).length > 0 && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-700/30 rounded-md border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {formData.messages.filter(m => m.trim()).length} message{formData.messages.filter(m => m.trim()).length !== 1 ? 's' : ''} loaded
                        </span>
                        <button
                          onClick={() => {
                            setFormData({ ...formData, messages: [] });
                            setBulkMessages('');
                          }}
                          className="text-xs text-red-600 hover:text-red-700 font-medium"
                        >
                          Clear All
                        </button>
                      </div>
                      <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Example (Message 1):</p>
                        <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {formData.messages.find(m => m.trim()) || ''}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Settings */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Settings</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Delay Min (seconds)
                      </label>
                      <input
                        type="number"
                        value={formData.settings.delayMin}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, delayMin: parseInt(e.target.value) || 10 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Delay Max (seconds)
                      </label>
                      <input
                        type="number"
                        value={formData.settings.delayMax}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, delayMax: parseInt(e.target.value) || 30 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Rest Interval (seconds)
                      </label>
                      <input
                        type="number"
                        value={formData.settings.restInterval}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, restInterval: parseInt(e.target.value) || 600 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Spam Pause Duration (seconds)
                      </label>
                      <input
                        type="number"
                        value={formData.settings.spamPauseDuration}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, spamPauseDuration: parseInt(e.target.value) || 86400 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Messages Per Batch
                      </label>
                      <input
                        type="number"
                        value={formData.settings.messagesPerBatch}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, messagesPerBatch: parseInt(e.target.value) || 15 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="1"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Number of DMs to send before pausing</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Batch Pause Duration (seconds)
                      </label>
                      <input
                        type="number"
                        value={formData.settings.batchPauseDuration}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, batchPauseDuration: parseInt(e.target.value) || 600 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="0"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Pause duration after each batch</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Max Requests Per Day (Per Account)
                      </label>
                      <input
                        type="number"
                        value={formData.settings.maxRequestsPerDay || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          settings: { ...formData.settings, maxRequestsPerDay: e.target.value ? parseInt(e.target.value) : null }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        min="1"
                        placeholder="Leave empty for unlimited"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Maximum requests per account per day (includes failed sends). Leave empty for unlimited. Resets every 24 hours.</p>
                    </div>
                    {/* Send Method Selection - Hidden, always uses REST */}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingTask(null);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateTask}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    {editingTask?.status === 'paused' ? 'Save & Resume' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recipient Process Modal */}
      {showProcessModal && (
        <RecipientProcessModal
          isOpen={showProcessModal}
          onClose={() => {
            setShowProcessModal(false);
            setPendingTaskData(null);
          }}
          processId={processId}
          onComplete={handleRecipientProcessComplete}
        />
      )}

      {/* Permission Denied Modal */}
      {showPermissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-yellow-100 dark:bg-yellow-900/20 rounded-full mb-4">
              <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
              Permission Required
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
              You do not have permission to use the Mass DM feature. Please contact support to enable this feature for your account.
            </p>
            <button
              onClick={() => setShowPermissionModal(false)}
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Recipient Process Modal Component
const RecipientProcessModal = ({ isOpen, onClose, processId, onComplete }) => {
  const [progress, setProgress] = useState({
    processed: 0,
    total: 0,
    stored: 0,
    duplicates: 0,
    inputDuplicates: 0,
    dbDuplicates: 0,
    errors: 0,
    currentUsername: '',
    status: 'processing',
    message: ''
  });
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket || !isOpen || !processId) return;

    const handleProgress = (data) => {
      if (data.processId === processId) {
        setProgress(prev => ({
          ...prev,
          processed: data.processed || prev.processed,
          total: data.total || prev.total,
          stored: data.stored || prev.stored,
          duplicates: data.duplicates || prev.duplicates,
          inputDuplicates: data.inputDuplicates !== undefined ? data.inputDuplicates : prev.inputDuplicates,
          dbDuplicates: data.dbDuplicates !== undefined ? data.dbDuplicates : prev.dbDuplicates,
          errors: data.errors || prev.errors,
          currentUsername: data.currentUsername || prev.currentUsername,
          status: data.status || prev.status,
          message: data.message || prev.message
        }));

        if (data.status === 'completed' || data.status === 'error') {
          setTimeout(() => {
            if (data.status === 'completed' && onComplete) {
              onComplete({
                stored: data.stored,
                duplicates: data.duplicates,
                inputDuplicates: data.inputDuplicates,
                dbDuplicates: data.dbDuplicates,
                errors: data.errors
              });
            }
            onClose();
          }, 2000);
        }
      }
    };

    socket.on('recipient-process-progress', handleProgress);

    return () => {
      socket.off('recipient-process-progress', handleProgress);
    };
  }, [socket, isOpen, processId, onClose, onComplete]);

  if (!isOpen) return null;

  const percentage = progress.total > 0 
    ? Math.round((progress.processed / progress.total) * 100) 
    : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Processing Recipients
        </h2>
        
        {progress.message && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded text-sm text-blue-800 dark:text-blue-200">
            {progress.message}
          </div>
        )}
        
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
            <span>Progress</span>
            <span>{progress.processed} / {progress.total} ({percentage}%)</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
            <div
              className="bg-rose-500 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>

        {progress.currentUsername && (
          <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            Processing: <span className="font-semibold">@{progress.currentUsername}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded">
            <div className="text-sm text-gray-600 dark:text-gray-400">Stored</div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {progress.stored}
            </div>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Duplicates</div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {progress.duplicates}
            </div>
          </div>
        </div>

        {(progress.inputDuplicates > 0 || progress.dbDuplicates > 0) && (
          <div className="mb-4 space-y-2">
            {progress.inputDuplicates > 0 && (
              <div className="text-xs text-gray-600 dark:text-gray-400">
                • {progress.inputDuplicates} duplicate(s) in input list
              </div>
            )}
            {progress.dbDuplicates > 0 && (
              <div className="text-xs text-gray-600 dark:text-gray-400">
                • {progress.dbDuplicates} already in database
              </div>
            )}
          </div>
        )}

        {progress.errors > 0 && (
          <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded mb-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Errors</div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {progress.errors}
            </div>
          </div>
        )}

        {progress.status === 'completed' && (
          <div className="text-green-600 dark:text-green-400 font-semibold text-center">
            ✓ Processing completed!
          </div>
        )}

        {progress.status === 'error' && (
          <div className="text-red-600 dark:text-red-400 font-semibold text-center">
            ✗ An error occurred during processing
          </div>
        )}

        {progress.status === 'processing' && (
          <div className="text-center text-gray-600 dark:text-gray-400 text-sm">
            Please wait...
          </div>
        )}
      </div>
    </div>
  );
};

export default MassDMs;

